import { Controller } from "react-hook-form";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { Form, SubmitButtonContainer } from "../../../../../layout/FormLayout";
import type { FormSource } from "./CreatePimProduct.utils";
import { InputWrapper } from "./CreatePimProduct.utils";
import { H5 } from "../../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import { z, z as zod } from "zod";
import {
  zodOptionalSelectBoxDefault,
  zodRequiredString,
} from "../../../../../util/zod.util";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import type {
  AttributeTemplateSummarySchema,
  PIMProductBase,
} from "../../../../../types/types.PIM";
import type { AxiosError } from "axios";
import axios from "axios";
import { endpoints } from "../../../../../endpoints";
import { PrimaryButtonLargeV2 } from "../../../../../components/Buttons/Buttons";
import { useHistory } from "react-router-dom";
import { useRoutePath } from "../../../../../util/Routing";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { strings } from "../../../../../util/strings";
import { RadioButton } from "../../../../../components/RadioButton/RadioButton";
import type { MinimalProductSummary } from "../ProductsList";

export const CreateNewProductPage = ({
  formSource,
}: {
  formSource: FormSource;
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const { notifySuccess, notifyError } = useNotifications();
  const history = useHistory();

  const PimNewProductSchema = zod
    .object({
      brand_name: zodRequiredString(t),
      product_template: z.object({
        value: z
          .object({
            id: z.string(),
            enable_variant_products: z.boolean(),
          })
          .required(),
        label: z.string().min(1, strings(t).thisIsARequiredField),
      }),
      pim_product_type: z
        .enum(["master", "variant", "standard"])
        .default("standard"),
      master_product: zodOptionalSelectBoxDefault.optional().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.pim_product_type === "variant" && !data.master_product?.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("This field is required"),
          path: ["master_product"],
        });
      }
    });

  type FormInputs = zod.infer<typeof PimNewProductSchema>;

  const { handleSubmit, register, formState, errors, control, watch, reset } =
    useFormWrapper<FormInputs>({
      resolver: zodResolver(PimNewProductSchema),
      defaultValues: {
        pim_product_type: "standard",
      },
    });

  const selectedTemplate = watch("product_template");

  const selectedPimProductType = watch("pim_product_type");

  const onSubmit = (data: FormInputs) => {
    setLoading(true);
    const url = endpoints.v2_tenants_id_pim_products(tenant_id);
    axios
      .post<PIMProductBase>(url, {
        name: data.brand_name.trim(),
        source: "template",
        template_id: selectedTemplate.value.id,
        pim_product_type: data.pim_product_type,
        ...(data.pim_product_type === "variant" && {
          product_id: data.master_product!.value,
        }),
      })
      .then((result) => {
        notifySuccess(
          `${result.data.name} ${t("Product created successfully")}`
        );
        setLoading(false);
        history.push(
          `${adminPath}/pim/products/${
            result.data.product_number ?? result.data.id
          }?source=new`
        );
      })
      .catch((error: AxiosError) => {
        notifyError(
          error.response
            ? error.response.data.message
            : t(`There was an error creating the product, please try again`),
          { error }
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    if (formSource === "new") {
      reset();
    }
  }, [formSource, reset]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <H5 style={{ marginTop: "25px" }}>{t("Product Details")}</H5>
      <InputWrapper>
        <TextField
          name="brand_name"
          label={t("Product Name")}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        <Controller
          as={SearchSelectInfiniteScroll}
          control={control}
          name="product_template"
          placeholder={t("Template")}
          id="product_template"
          rules={{
            required: true,
          }}
          baseUrl={endpoints.v2_storefronts_id_pim_templates_summary(tenant_id)}
          params={
            new URLSearchParams({
              order_by: "asc",
            })
          }
          getOptions={(response: AttributeTemplateSummarySchema[]) =>
            response.map(
              ({ template_name, template_id, enable_variant_products }) => ({
                value: {
                  id: template_id,
                  enable_variant_products: enable_variant_products ?? false,
                },
                label: template_name,
              })
            )
          }
          defaultValue={{ value: "", label: "" }}
          errors={errors}
          formState={formState}
        />
        {selectedTemplate?.value &&
          selectedTemplate.value.enable_variant_products && (
            <>
              <H5>{t("Product Type")}</H5>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name="pim_product_type"
                    value="standard"
                    optionTitle={t("Standalone")}
                    theref={register()}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name="pim_product_type"
                    value="master"
                    optionTitle={t("Master")}
                    theref={register()}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name="pim_product_type"
                    value="variant"
                    optionTitle={t("Variant")}
                    theref={register()}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
            </>
          )}
        {selectedPimProductType === "variant" && (
          <Controller
            as={SearchSelectInfiniteScroll}
            control={control}
            name="master_product"
            placeholder={t("Master Product")}
            id="master_product"
            rules={{
              required: true,
            }}
            baseUrl={endpoints.v2_tenants_id_pim_products_list_minimal(
              tenant_id
            )}
            params={(() => {
              const params = new URLSearchParams({
                show_only: "master",
                published_products_only: "true",
                template: selectedTemplate.label,
                status: "published",
              });
              params.append("status", "staged");
              return params;
            })()}
            getOptions={(response: MinimalProductSummary[]) =>
              response.map(({ name, id }) => ({
                value: id,
                label: name,
              }))
            }
            defaultValue={{ value: "", label: "" }}
            errors={errors}
            formState={formState}
          />
        )}
      </InputWrapper>
      <SubmitButtonContainer>
        <PrimaryButtonLargeV2 loading={loading}>
          {t("Create Product")}
        </PrimaryButtonLargeV2>
      </SubmitButtonContainer>
    </Form>
  );
};
